import React, { useState, useRef, useEffect } from 'react';
import { Card, Input, Button, List, Avatar, Space, Typography, Upload, message as antdMessage, Image } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined, ReloadOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { aiAPI } from '../services/api';

const { Text } = Typography;

export default function AIChat() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: '你好，我是AI智能助手。请问你想了解什么？' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageBase64, setImageBase64] = useState(null);
  const listRef = useRef(null);

  const sendMessage = async () => {
    const question = inputValue.trim();
    if (!question || loading) return;

    const newMessages = [...messages, { role: 'user', content: question }];
    setMessages(newMessages);
    setInputValue('');
    setLoading(true);
    try {
      const res = await aiAPI.ask({ question, imageBase64 });
      const answer = res?.data?.answer || '抱歉，暂时无法回答。';
      setMessages(prev => [...prev, { role: 'assistant', content: answer }]);
    } catch (err) {
      let errorText = '请求失败，请稍后重试。';
      
      // 根据错误类型提供更详细的提示
      if (err.code === 'ERR_NETWORK') {
        errorText = '网络连接失败：无法连接到服务器，请检查网络连接或确认后端服务是否启动。';
      } else if (err.code === 'ERR_CONNECTION_REFUSED') {
        errorText = '连接被拒绝：后端服务器可能未启动，请检查服务状态。';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorText = '请求超时：服务器响应时间过长，请稍后重试。';
      } else if (err.response?.status === 500) {
        const serverMsg = err?.response?.data?.message;
        const serverDetail = err?.response?.data?.detail;
        if (serverDetail?.error?.message?.includes('API key')) {
          errorText = 'OpenAI API密钥错误：请检查后端配置中的OPENAI_API_KEY是否正确。';
        } else if (serverDetail?.error?.message?.includes('quota')) {
          errorText = 'OpenAI配额不足：请检查账户余额或使用量限制。';
        } else {
          errorText = `服务器错误：${serverDetail?.error?.message || serverDetail?.message || serverMsg || '内部服务器错误'}`;
        }
      } else if (err.response?.status === 400) {
        const serverMsg = err?.response?.data?.message;
        errorText = `请求参数错误：${serverMsg || '请检查输入内容'}`;
      } else if (err.response?.status === 401) {
        errorText = '认证失败：请重新登录。';
      } else if (err.response?.status === 403) {
        errorText = '权限不足：您没有访问此功能的权限。';
      } else if (err.response?.status === 404) {
        errorText = '接口不存在：AI服务可能未正确配置。';
      } else if (err.response?.data) {
        const serverMsg = err?.response?.data?.message;
        const serverDetail = err?.response?.data?.detail;
        errorText = serverDetail?.error?.message || serverDetail?.message || serverMsg || err?.message || errorText;
      }
      
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${errorText}` }]);
    } finally {
      setLoading(false);
      setImageBase64(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{ role: 'assistant', content: '你好，我是AI智能助手。请问你想了解什么？' }]);
    setImageBase64(null);
  };

  const handlePaste = async (e) => {
    if (!e.clipboardData) return;
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          const base64 = await fileToBase64(file);
          setImageBase64(base64);
          antdMessage.success('已粘贴图片');
        }
      }
    }
  };

  const beforeUpload = async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      antdMessage.error('仅支持图片文件');
      return Upload.LIST_IGNORE;
    }
    const base64 = await fileToBase64(file);
    setImageBase64(base64);
    antdMessage.success('已选择图片');
    return Upload.LIST_IGNORE;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <Card
      title={
        <Space>
          <RobotOutlined />
          <span>AI智能问答</span>
        </Space>
      }
      extra={
        <Button icon={<ReloadOutlined />} size="small" onClick={clearChat} disabled={loading}>
          清空
        </Button>
      }
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: 640 }}>
        {/* 服务器端统一配置OpenAI Key，无需本地输入 */}
        <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#fafafa' }}>
          <List
            dataSource={messages}
            renderItem={(item) => (
              <List.Item style={{ border: 'none', padding: '8px 0' }}>
                <Space align="start" style={{ width: '100%' }}>
                  <Avatar icon={item.role === 'assistant' ? <RobotOutlined /> : <UserOutlined />} />
                  <div style={{
                    background: item.role === 'assistant' ? '#fff' : '#e6f7ff',
                    border: '1px solid #f0f0f0',
                    padding: '8px 12px',
                    borderRadius: 8,
                    maxWidth: '80%'
                  }}>
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{item.content}</Text>
                  </div>
                </Space>
              </List.Item>
            )}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderTop: '1px solid #f0f0f0' }}>
          {imageBase64 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Image src={imageBase64} width={120} style={{ border: '1px solid #eee' }} />
              <Button icon={<DeleteOutlined />} onClick={() => setImageBase64(null)}>移除图片</Button>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Input.TextArea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => { handleKeyDown(e); }}
              onPaste={handlePaste}
              autoSize={{ minRows: 1, maxRows: 4 }}
              placeholder="请输入问题，可粘贴截图，按 Enter 发送（Shift+Enter 换行）"
              disabled={loading}
            />
            <Upload beforeUpload={beforeUpload} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />}>上传截图</Button>
            </Upload>
            <Button type="primary" icon={<SendOutlined />} onClick={sendMessage} loading={loading}>
              发送
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}


